import React from 'react';
import { useTranslation } from 'react-i18next';
import { XStack, YStack, Text } from 'tamagui';
import { tokens } from 'tamagui-config';

import { EnergyCostChart } from './EnergyCostChart';
import { InvalidConfiguration } from '../../../components/common/InvalidConfiguration';
import { LoadingOverlay } from '../../../components/common/LoadingOverlay';
import { useGetEnergySavingData } from '../../../hooks';
const Dot = ({ color }: { color: string }) => (
  <YStack width={12} height={12} borderRadius={6} backgroundColor={color} marginRight={4} />
);

export const EnergySavingContent: React.FC = () => {
  const { t } = useTranslation();
  const { data, loading } = useGetEnergySavingData();

  return (
    <YStack flex={1} gap="$4">
      <LoadingOverlay isVisible={loading} />

      {!loading && !data && <InvalidConfiguration />}

      {!loading &&
        data &&
        (() => {
          const aggregateDailyData = (rawData: { label: string; value: number }[]) => {
            const dailyTotals = new Map<string, number>();

            rawData.forEach((item) => {
              const date = item.label;
              const currentTotal = dailyTotals.get(date) || 0;
              dailyTotals.set(date, currentTotal + item.value);
            });

            return Array.from(dailyTotals.entries()).map(([date, total]) => ({
              label: date,
              value: Number(total.toFixed(2)),
            }));
          };

          const aggregatedData = aggregateDailyData(data);

          const dataByDate = new Map<string, number>();
          aggregatedData.forEach((item) => {
            dataByDate.set(item.label, item.value);
          });

          const today = new Date();

          const thisWeekData: { label: string; value: number }[] = [];
          for (let i = 7; i >= 1; i--) {
            const targetDate = new Date(today);
            targetDate.setDate(today.getDate() - i);
            const dateString = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;

            thisWeekData.push({
              label: dateString,
              value: dataByDate.get(dateString) || 0,
            });
          }

          const lastWeekData: { label: string; value: number }[] = [];
          for (let i = 14; i >= 8; i--) {
            const targetDate = new Date(today);
            targetDate.setDate(today.getDate() - i);
            const dateString = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;

            lastWeekData.push({
              label: dateString,
              value: dataByDate.get(dateString) || 0,
            });
          }

          const firstWeekData = lastWeekData;
          const secondWeekData = thisWeekData;

          return (
            <YStack gap="$3">
              {/* Legend */}
              <XStack alignItems="center" gap="$3">
                <XStack alignItems="center" gap="$2">
                  <Dot color={tokens.color.hgPrimary} />
                  <Text fontSize="$7.5" color="$white12">
                    {t('electrical.dailyCost')}
                  </Text>
                </XStack>
                <XStack alignItems="center" gap="$2">
                  <Dot color={tokens.color.hgTertiary} />
                  <Text fontSize="$7.5" color="$white12">
                    {t('electrical.lastWeek')}
                  </Text>
                </XStack>
              </XStack>

              {/* Chart */}
              <EnergyCostChart thisWeekCosts={secondWeekData} lastWeekCosts={firstWeekData} />
            </YStack>
          );
        })()}
    </YStack>
  );
};
